import { authApiPost } from '@/lib/api/auth-api-client';
import { clients, HttpError } from '@/lib/https';
import { useAuthStore } from '@/state/store/auth';
import { clearAuthStorage } from '@/state/store/auth/auth-storage';
import {
  AccountActivationPayload,
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  SigninEmailPayload,
  SigninEmailResponse,
  ISignupByEmailPayload,
  ISignupByEmailResponse,
  IGetSignUpSettingResponse,
  ActivationCodeExpirationResponse,
} from '../types/auth.type';
export type { ActivationCodeExpirationResponse };

/**
 * Authentication API Utilities
 *
 * A set of low-level API functions for handling authentication-related operations such as signing in (with or without MFA),
 * signing out, refreshing tokens, and managing account lifecycle actions (activation, password reset, etc.).
 *
 * Functions:
 * - `signin`: Signs in a user using either password or MFA-based flow
 * - `signout`: Logs out the user and removes relevant local storage
 * - `getRefreshToken`: Refreshes the user's access token using a stored refresh token
 * - `accountActivation`: Activates a new account using password, code, and CAPTCHA
 * - `forgotPassword`: Triggers a password recovery email
 * - `resetPassword`: Resets the user password and logs them out from all devices
 * - `resendActivation`: Resends the account activation link
 * - `logoutAll`: Logs the user out from all devices
 *
 * Interfaces & Types:
 * - `SignInResponse`: Response from password-based sign-in
 * - `MFASigninResponse`: Response from MFA-based sign-in
 * - `PasswordSigninPayload`, `MFASigninPayload`: Payloads for sign-in methods
 * - `AccountActivationPayload`: Data used for account activation request
 *
 * Features:
 * - Uses `fetch` or `clients.post` for API communication
 * - All endpoints are protected with proper headers and credentials
 * - Throws structured `HttpError` for non-OK responses
 * - Supports CAPTCHA where applicable
 *
 * Example:
 * ```ts
 * // Password Sign-in
 * const result = await signin<'password'>({
 *   grantType: 'password',
 *   username: 'user@example.com',
 *   password: 'secret',
 *   captchaToken: 'captcha-token'
 * });
 *
 * // Reset Password
 * await resetPassword({ code: '123456', password: 'newPassword' });
 * ```
 *
 */

export interface SignInResponse {
  access_token: string;
  refresh_token: string;
  enable_mfa: boolean;
  mfaId: string;
  mfaType: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  sso_user_redirect_url?: string;
}

export type PasswordSigninPayload = {
  grantType: 'password';
  username: string;
  password: string;
  captchaToken?: string;
};

export type SSoSigninPayload = {
  grantType: 'social';
  code: string;
  state: string;
  provider?: string;
};

export type SSoConsentSigninPayload = {
  grantType: 'sso_consent';
  code: string;
};

export type MFASigninPayload = {
  grantType: 'mfa_code';
  code: string;
  mfaId: string;
  mfaType: number;
};

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

export interface SigninBySSOPayload {
  grantType: 'social';
  code: string;
  state: string;
  provider?: string;
}

export interface SigninByBlocksOidcPayload {
  grantType: 'authorization_code';
  code: string;
}

const projectKey = import.meta.env.VITE_X_BLOCKS_KEY || '';
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
const oidcClientId = import.meta.env.VITE_OIDC_CLIENT_ID || '';

const getApiUrl = (path: string) => {
  const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;

  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${cleanPath}`;
};

export const savedOrgId =
  typeof window !== 'undefined' ? window.localStorage.getItem('selected-org-id') : null;

export const signin = async <
  T extends 'password' | 'social' | 'mfa_code' | 'authorization_code' | 'sso_consent' = 'password',
>(
  payload:
    | PasswordSigninPayload
    | MFASigninPayload
    | SigninBySSOPayload
    | SigninByBlocksOidcPayload
    | SSoConsentSigninPayload
): Promise<
  T extends 'password' | 'social' | 'sso_consent' ? SignInResponse : MFASigninResponse
> => {
  // legacy multi-grant token endpoint — retired; authorization_code and sso_consent
  // grants below still target it and are known-broken until re-architected on the
  // hosted OIDC flow (see blocks-iam-sso-oidc-implementation)
  const url = getApiUrl('/idp/v1/Authentication/Token');

  // sign in flow
  if (payload.grantType === 'password') {
    const response = await fetch(getApiUrl('/iam/v4/auth/login'), {
      method: 'POST',
      body: JSON.stringify({
        ClientId: oidcClientId,
        username: payload.username,
        password: payload.password,
        captcha_code: payload.captchaToken,
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else if (payload.grantType === 'social') {
    const response = await fetch(getApiUrl('/iam/v4/auth/social/callback'), {
      method: 'POST',
      body: JSON.stringify({
        code: payload.code,
        state: payload.state,
        provider: payload.provider,
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else if (payload.grantType === 'authorization_code') {
    const signinBySSOData = new URLSearchParams();
    signinBySSOData.append('grant_type', 'authorization_code');
    signinBySSOData.append('code', payload.code);

    if (savedOrgId) {
      signinBySSOData.append('org_id', savedOrgId);
    }
    const response = await fetch(url, {
      method: 'POST',
      body: signinBySSOData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json();
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else if (payload.grantType === 'sso_consent') {
    const ssoConsentData = new URLSearchParams();
    ssoConsentData.append('grant_type', 'sso_consent');
    ssoConsentData.append('code', payload.code);

    if (savedOrgId) {
      ssoConsentData.append('org_id', savedOrgId);
    }
    const response = await fetch(url, {
      method: 'POST',
      body: ssoConsentData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
    });
    if (!response.ok) {
      const err = await response.json();
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else {
    // MFA OTP Verification flow — second call to /auth/login with only the
    // mfa_* fields, per the shared EmbeddedLoginRequest contract
    const response = await fetch(getApiUrl('/iam/v4/auth/login'), {
      method: 'POST',
      body: JSON.stringify({
        ClientId: oidcClientId,
        mfa_id: payload.mfaId,
        mfa_code: payload.code || '',
        mfa_type: payload.mfaType,
      }),
      headers: {
        'Content-Type': 'application/json',
        'x-blocks-key': projectKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      throw new HttpError(response.status, err);
    }

    return response.json();
  }
};

export const signout = async (): Promise<{ isSuccess: true }> => {
  try {
    clearAuthStorage();
    const url = '/iam/v4/auth/logout';
    return await clients.post(
      url,
      JSON.stringify({
        refreshToken: useAuthStore.getState().refreshToken,
      })
    );
  } catch (error) {
    console.error('Logout operation failed:', error);
    throw error;
  }
};

export const getRefreshToken = async () => {
  const response = await fetch(getApiUrl('/iam/v4/auth/refresh'), {
    method: 'POST',
    body: JSON.stringify({
      refresh_token: useAuthStore.getState().refreshToken ?? '',
    }),
    headers: {
      'Content-Type': 'application/json',
      'x-blocks-key': projectKey,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const err = await response.json();
    throw new HttpError(response.status, err);
  }

  return response.json();
};

export const validateActivationCode = async (payload: {
  activationCode: string;
  projectKey: string;
}): Promise<ActivationCodeExpirationResponse> => {
  const url = '/iam/v4/auth/validate-activation';
  return clients.post(url, JSON.stringify({ activationCode: payload.activationCode }));
};

export const accountActivation = async (data: AccountActivationPayload) => {
  const organizationId = data.organizationId?.trim();
  const payload = {
    code: data.code,
    password: data.password,
    captchaCode: data.captchaCode,
    firstName: data.firstname,
    lastName: data.lastname,
    preventPostEvent: true,
    ...(organizationId ? { organizationId } : {}),
  };
  const url = '/iam/v4/auth/activate';
  return clients.post(url, JSON.stringify(payload));
};

export const forgotPassword = async (
  payload: ForgotPasswordPayload
): Promise<ForgotPasswordResponse> => {
  const url = '/iam/v4/auth/recover';
  return clients.post(url, JSON.stringify({ email: payload.email, captchaCode: payload.captchaCode }));
};

export const resetPassword = async (data: { code: string; password: string }) => {
  const payload = {
    ...data,
    logoutFromAllDevices: true,
  };

  const url = '/iam/v4/auth/reset-password';
  return clients.post(url, JSON.stringify(payload));
};

export const resendActivation = async (data: { userId: string; projectKey?: string }) => {
  const url = '/iam/v4/auth/resend-activation';
  return clients.post(url, JSON.stringify({ userId: data.userId }));
};

export const logoutAll = async () => {
  const url = '/iam/v4/auth/logout-all';
  return clients.post(url, JSON.stringify({}));
};

export const signinByEmail = (payload: SigninEmailPayload): Promise<SigninEmailResponse> => {
  const url = '/iam/v4/auth/login';
  return clients.post(
    url,
    JSON.stringify({
      ClientId: oidcClientId,
      username: payload.username,
      password: payload.password,
      captcha_code: payload.captchaCode,
    })
  );
};

export const switchOrganization = async (orgId: string): Promise<MFASigninResponse> => {
  return authApiPost<MFASigninResponse>('/switch-org', { organization_id: orgId });
};

export const signupByEmail = (payload: ISignupByEmailPayload): Promise<ISignupByEmailResponse> => {
  const organizationId = payload.organizationId?.trim();
  return clients.post(
    '/iam/v4/auth/signup',
    JSON.stringify({
      email: payload.email,
      captchaCode: payload.captchaCode,
      ...(organizationId ? { organizationId } : {}),
    })
  );
};

export const getSignupSettings = (): Promise<IGetSignUpSettingResponse> => {
  return clients.get('/iam/v4/iam/signup-settings');
};
